"use client";

import { useTranslations } from "next-intl";
import Image from "next/image";
import { createSerializer, useQueryStates } from "nuqs";
import { use } from "react";

import { TripadvisorRating } from "~/components/tripadvisor-rating";
import { buttonVariants } from "~/components/ui/button";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { Link } from "~/navigation";

import { tourSearchParams } from "../_lib/search-params";
import type { getTours } from "../_queries";

export function Results({
  resultsPromise,
}: {
  resultsPromise: ReturnType<typeof getTours>;
}) {
  const { results, total } = use(resultsPromise);
  const serialize = createSerializer(tourSearchParams);
  const [{ page, ...searchParams }] = useQueryStates(tourSearchParams, {
    shallow: false,
    throttleMs: 400,
    scroll: true,
  });
  const t = useTranslations("TourDetails");
  const tTours = useTranslations("Tours");
  const tCommon = useTranslations("Common");
  const formatCurrency = useCurrencyFormatter();
  if (results.length === 0) {
    return (
      <div className="mx-auto mt-8 px-6 text-center">
        <h2 className="text-2xl font-medium">{tTours("noToursFound")}</h2>
        <p className="text-muted mt-4 text-lg">{tTours("pleaseWidenSearch")}</p>
      </div>
    );
  }
  const totalPages = Math.ceil(total / 50);

  return (
    <>
      <div className="mb-4 flex justify-end text-sm font-medium">
        {tTours("searchResultsCount", { count: total })}
      </div>
      <div className="grid grid-cols-2 gap-x-2 gap-y-5 sm:gap-x-4 sm:gap-y-10 md:grid-cols-2 lg:grid-cols-3">
        {results.map(
          ({
            id,
            inventory,
            title,
            imageUrl,
            slug,
            perPerson,
            tripadvisorRating,
            locationName,
          }) => (
            <Link key={id} href={`/tours/${slug}/${id}`} prefetch={false}>
              {imageUrl ? (
                <Image
                  alt=""
                  className="aspect-4/5 mb-4 w-full rounded-lg object-cover"
                  height={528}
                  src={imageUrl}
                  unoptimized
                  width={440}
                />
              ) : null}
              <div className="flex flex-col gap-1">
                <div className="text-muted flex items-center justify-between gap-1 text-sm font-light leading-none">
                  {tripadvisorRating ? (
                    <TripadvisorRating
                      size="small"
                      value={tripadvisorRating / 10}
                    />
                  ) : null}
                  <div>{locationName}</div>
                </div>
                <div className="line-clamp-3 font-medium leading-tight">
                  {title}
                </div>
                <div className="text-muted flex items-center gap-1 text-sm font-light leading-tight">
                  {t("fromAmount", {
                    currencyAmount: formatCurrency(inventory),
                  })}{" "}
                  {perPerson ? t("perPerson") : t("perGroup")}
                </div>
              </div>
            </Link>
          ),
        )}
      </div>
      {totalPages > page || page > 1 ? (
        <div className="mt-8 flex justify-center gap-4">
          {page > 1 ? (
            <Link
              className={buttonVariants()}
              href={`/tours${serialize({ page: page - 1, ...searchParams })}`}
            >
              {tTours("previous")}
            </Link>
          ) : null}
          {page < totalPages ? (
            <Link
              className={buttonVariants()}
              href={`/tours${serialize({ page: page + 1, ...searchParams })}`}
            >
              {tCommon("next")}
            </Link>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
